import { useEffect, useMemo, useState } from 'react'
import { Badge, HStack, Stack } from '@chakra-ui/react'

import { Dropzone } from 'components'
import CustomFormFileCardToUploadFile from 'components/tools/CustomForm/commonComponents/CustomFormFileCardToUploadFile'
import CustomFormFileCardToGetFile from 'components/tools/CustomForm/commonComponents/CustomFormFileCardToGetFile'
import { CustomFormViewerQuestionProps } from 'components/tools/CustomForm/types/CustomFormViewerQuestionProps'
import { useCustomFormViewer } from 'components/tools/CustomForm/components/Viewer/contexts/CustomFormViewerManager/Provider'
import { UploadFile } from 'types'
import { convertBytesNumberToString } from 'utils/transforms/number'
import validateSingleFileUploadOnCustomForm from 'components/tools/CustomForm/components/Viewer/utils/validateSingleFileUploadOnCustomForm'
import CustomFormFieldErrorMessage from 'components/tools/CustomForm/commonComponents/CustomFormFieldErrorMessage'
import useCustomFormHandleErrorWithScrollBehavior from '../../hooks/useCustomFormHandleErrorWithScrollBehavior'

export default function CustomFormSingleUploadQuestion({
  question,
  questionKey,
  formMethods,
  disabled,
}: CustomFormViewerQuestionProps) {
  const [file, setFile] = useState<UploadFile>()

  const { removeErrorFromQuestion, setErrorOnQuestion, thisQuestionHasError } = useCustomFormViewer()

  const questionWithError = thisQuestionHasError(question.slug)?.question

  async function onUpload(file: UploadFile) {
    if (!file) return
    const errorMessage = validateSingleFileUploadOnCustomForm({ file, question })

    if (errorMessage === '') {
      setFile(file)
      removeErrorFromQuestion(question.slug)
    } else {
      setErrorOnQuestion(question, errorMessage)
    }
  }

  const fileValues = useMemo(() => {
    return question?.answer ? question?.answer : undefined
  }, [])

  const [fileId, fileSlug, archiveId] = fileValues || [0, 0, 0]

  useEffect(() => {
    if (formMethods?.setValue) {
      if (question?.answer) {
        formMethods?.setValue(questionKey, question?.answer)
      } else {
        formMethods?.setValue(questionKey, [])
      }
    }
  }, [question?.answer])

  useCustomFormHandleErrorWithScrollBehavior({ questionWithError, questionSlug: question.slug })

  return (
    <Stack id={question.slug}>
      <Dropzone
        isDisabled={disabled}
        onUpload={onUpload}
        inputProps={{ accept: question.validation?.have_camera ? 'image/*' : '*' }}
      />

      {file && <CustomFormFileCardToUploadFile questionKey={questionKey} currentFile={file} />}

      {fileValues && fileValues.length > 0 && !file && (
        <CustomFormFileCardToGetFile
          fileId={fileId as string}
          fileSlug={fileSlug as string}
          archiveId={archiveId as string}
        />
      )}

      <CustomFormFieldErrorMessage message={questionWithError?.error_message} />

      {question.validation && (
        <HStack flexWrap="wrap">
          {question.validation.min && (
            <Badge textTransform="none">Tamanho mínimo ({convertBytesNumberToString(question.validation.min)})</Badge>
          )}
          {question.validation.max && (
            <Badge textTransform="none">Tamanho máximo ({convertBytesNumberToString(question.validation.max)})</Badge>
          )}
          {question.validation.extensions && (
            <Badge textTransform="none">Extensões permitidas ({question.validation.extensions?.join(', ')})</Badge>
          )}
        </HStack>
      )}
    </Stack>
  )
}
